//Onboarding.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { getEmployeeById, updateEmployeeById } from "../firebase/employees";
import "../styles/Onboarding.css";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { parsePlace } from "../utils/parsePlace";
import { db } from "../firebase/config";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import Oiiko from "../tools/oiikotranslogo2.png";
import Oiiko2 from "../tools/oiikotranstxt.png";

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "nonbinary", label: "Non-binary" },
  { value: "na", label: "Prefer not to say" },
  { value: "other", label: "Other" },
];

const groupOptions = [
  { value: "IT", label: "IT" },
  { value: "Finance", label: "Finance" },
  { value: "Sales", label: "Sales" },
  { value: "Marketing", label: "Marketing" },
  { value: "Operations", label: "Operations" },
];

const onlyDigits = (s = "") => s.replace(/\D+/g, "");
const formatPhone = (value) => {
  const d = onlyDigits(value).slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const validPhone = (value) => onlyDigits(value).length === 10;

const isAdult = (yyyy_mm_dd, minYears = 16) => {
  if (!yyyy_mm_dd) return false;
  const birth = new Date(yyyy_mm_dd);
  if (Number.isNaN(+birth)) return false;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - minYears);
  return birth <= cutoff;
};

export default function Onboarding() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const employeeDocId = user?.employeeDocId || user?.uid;

  const [step, setStep] = useState(1);
  const [employee, setEmployee] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState({ jobTitle: false, group: false });

  const [structuredAddr, setStructuredAddr] = useState({
    street: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    lat: null,
    lng: null,
  });

  const [formData, setFormData] = useState({
    preferredFirstName: "",
    phone: "",
    gender: "",
    address: "",
    birthDate: "",
    jobTitle: "",
    group: "",
  });

  useEffect(() => {
    if (!loading && user?.companyId && employeeDocId) {
      (async () => {
        try {
          const emp = await getEmployeeById(user.companyId, employeeDocId);
          if (!emp) return;

          setEmployee(emp);
          setFormData((prev) => ({
            ...prev,
            preferredFirstName: emp.preferredFirstName || "",
            phone: emp.phone || "",
            gender: emp.gender || "",
            address: emp.address || "",
            birthDate: emp.birthDate || "",
            jobTitle: emp.jobTitle || "",
            group: emp.group || "",
          }));

          setLocked({
            jobTitle: !!emp.jobTitle,
            group: !!emp.group,
          });
        } catch (e) {
          console.error("Failed to load employee:", e);
        }
      })();
    }
  }, [user, loading, employeeDocId]);

  const validateStep = () => {
    const stepErrors = {};

    if (step === 1) {
      if (!formData.preferredFirstName?.trim()) {
        stepErrors.preferredFirstName =
          "Please enter your preferred first name.";
      }
      if (!validPhone(formData.phone)) {
        stepErrors.phone = "Enter a valid 10-digit phone number.";
      }
      if (!formData.gender?.trim()) {
        stepErrors.gender =
          "Please select your gender (or choose 'Prefer not to say').";
      }
      if (!formData.address?.trim()) {
        stepErrors.address = "Please provide your address.";
      }
      if (!formData.birthDate?.trim() || !isAdult(formData.birthDate, 16)) {
        stepErrors.birthDate = "Enter a valid birth date (16+).";
      }
    }

    if (step === 2) {
      if (!formData.jobTitle?.trim())
        stepErrors.jobTitle = "Job title is required.";
      if (!formData.group?.trim()) stepErrors.group = "Select a group.";
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep((prev) => prev + 1);
      setErrors({});
    }
  };

  const handlePrev = () => setStep((prev) => prev - 1);

  const geocodeIfNeeded = async () => {
    if (structuredAddr.lat && structuredAddr.lng) return structuredAddr;

    const queryStr = formData.address?.trim();
    if (!queryStr) return null;

    try {
      const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
      url.searchParams.set("address", queryStr);
      url.searchParams.set(
        "key",
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
      );

      const res = await fetch(url.toString());
      const data = await res.json();
      if (data.status !== "OK" || !data.results?.length) return null;

      const r = data.results[0];
      const comps = r.address_components || [];
      const get = (...types) =>
        comps.find((c) => c.types?.some((t) => types.includes(t)));

      const number = get("street_number")?.long_name || "";
      const route = get("route")?.long_name || "";
      const street = number && route ? `${number} ${route}` : route || "";

      const city =
        get("locality")?.long_name ||
        get("sublocality")?.long_name ||
        get("administrative_area_level_2")?.long_name ||
        "";

      const stateProvince =
        get("administrative_area_level_1")?.short_name ||
        get("administrative_area_level_1")?.long_name ||
        "";

      const postalCode = get("postal_code")?.long_name || "";
      const country = get("country")?.long_name || "";

      return {
        street,
        city,
        stateProvince,
        postalCode,
        country,
        lat: r.geometry?.location?.lat ?? null,
        lng: r.geometry?.location?.lng ?? null,
      };
    } catch (e) {
      console.error("Geocoding error:", e);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    if (!user?.companyId || !employeeDocId) {
      setErrors((prev) => ({
        ...prev,
        submit: "Missing user context. Please re-login and try again.",
      }));
      return;
    }

    setSaving(true);

    try {
      const parsed = await geocodeIfNeeded();

      if (!parsed || !parsed.lat || !parsed.lng) {
        setSaving(false);
        setErrors((prev) => ({
          ...prev,
          address:
            "Please select a suggested address or enter a valid address.",
        }));
        return;
      }

      const addressString =
        formData.address ||
        `${parsed.street}, ${parsed.city}, ${parsed.stateProvince} ${parsed.postalCode}, ${parsed.country}`;

      // ✅ Build a single payload used for BOTH docs
      const onboardingPayload = {
        ...formData,
        address: addressString,
        addressDetails: {
          street: parsed.street,
          city: parsed.city,
          stateProvince: parsed.stateProvince,
          postalCode: parsed.postalCode,
          country: parsed.country,
          geo: { lat: parsed.lat, lng: parsed.lng },
        },
        profileCompleted: true,
        status: "Active",
        updatedAt: serverTimestamp(),
      };

      // 1) ✅ Update company employee doc
      await updateEmployeeById(
        user.companyId,
        employeeDocId,
        onboardingPayload,
      );

      // 2) ✅ Update users/{uid} doc (THIS is what your guard is reading)
      // Using setDoc(..., { merge:true }) so it works whether fields exist or not
      const userRef = doc(db, "users", employeeDocId);
      await setDoc(
        userRef,
        {
          ...onboardingPayload,

          // make sure these stay correct on the user doc
          uid: employeeDocId,
          employeeDocId, // you already store this
          companyId: user.companyId,
          role: "employee",
        },
        { merge: true },
      );

      // ✅ Now routing should succeed because users.profileCompleted is true
      navigate("/employee-dashboard", { replace: true });
    } catch (err) {
      console.error("Onboarding failed:", err?.message || err);
      setErrors((prev) => ({
        ...prev,
        submit: "Something went wrong saving your profile. Please try again.",
      }));
    } finally {
      setSaving(false);
    }
  };
  const ensureOption = (options, currentValue) => {
    if (!currentValue) return options;
    const exists = options.some((o) => o.value === currentValue);
    return exists
      ? options
      : [{ value: currentValue, label: currentValue }, ...options];
  };

  // ✅ FIXED floating-label input renderer:
  // label MUST be AFTER input for input:focus + label CSS
  // placeholder MUST be " " so :placeholder-shown works
  const renderInput = (
    label,
    key,
    type = "text",
    { placeholder, helper, readOnly = false, inputMode } = {},
  ) => (
    <div className="onboarding-field" key={key}>
      <input
        type={type}
        value={formData[key]}
        placeholder={placeholder ?? " "}
        inputMode={inputMode}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        className={errors[key] ? "error" : ""}
        readOnly={readOnly}
      />
      <label>{label}</label>

      {helper && <small className="helper">{helper}</small>}
      {errors[key] && <span className="error-text">{errors[key]}</span>}
    </div>
  );

  // ✅ FIXED select renderer with data-hasvalue
  const renderSelect = (
    label,
    key,
    options,
    { disabled = false, helper } = {},
  ) => (
    <div className="onboarding-field" key={key}>
      <select
        value={formData[key]}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        disabled={disabled}
        className={errors[key] ? "error" : ""}
        data-hasvalue={!!formData[key]}
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <label>{label}</label>

      {helper && <small className="helper">{helper}</small>}
      {errors[key] && <span className="error-text">{errors[key]}</span>}
    </div>
  );

  if (!loading && user && !user.companyId) {
    return (
      <div className="onboarding-wrapper-container">
        <div className="onboarding-wrapper">
          <div className="onboarding-left">
            <h1 className="logo">OIIKO</h1>
            <h2>Welcome, {employee?.preferredFirstName || "Addy"}</h2>
            <p>Complete Your Onboarding Profile</p>
          </div>

          <div className="onboarding-right">
            <div className="onboarding-form">
              <p style={{ color: "#b00", marginTop: "1rem" }}>
                Your profile isn’t linked to a company yet. Please contact your
                employer or sign out and back in.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-wrapper-container">
      <div className="onboarding-wrapper">
        <div className="onboarding-left">
          <div className="left-overlay" />

          <div className="onboarding-left-inner">
            {/* Brand stack */}
            <div className="brand-stack">
              <div className="brand-mark">
                <img src={Oiiko} alt="Oiiko logo" className="brand-logoo" />
              </div>

              <div className="brand-wordmark">
                <img src={Oiiko2} alt="Oiiko" className="brand-text" />
              </div>
            </div>

            <div className="left-divider" />

            {/* Welcome copy */}
            <div className="left-copy">
              <h2 className="left-title">Welcome{employee?.name ? "," : ""}</h2>

              <p className="left-sub">
                Let’s complete your profile so your employer can schedule you,
                track time, and keep everything accurate.
              </p>
            </div>

            {/* Step hint */}
            <div className="left-step">
              <span className="left-step-chip">Step {step} of 3</span>
              <span className="left-step-label">
                {step === 1
                  ? "Basic Details"
                  : step === 2
                  ? "Role"
                  : "Review & Submit"}
              </span>
            </div>
          </div>
        </div>

        <div className="onboarding-right">
          <div className="onboarding-steps">
            <div className={`step-circle ${step >= 1 ? "active" : ""}`} />
            <div className={`step-circle ${step >= 2 ? "active" : ""}`} />
            <div className={`step-circle ${step >= 3 ? "active" : ""}`} />
          </div>

          <form className="onboarding-form" onSubmit={handleSubmit}>
            <h2 className="onboarding-form-title">
              {step === 1
                ? "Basic Details"
                : step === 2
                ? "Role"
                : "Review & Submit"}
            </h2>

            {step === 1 && (
              <div className="onboarding-form-grid">
                {renderInput("Preferred First Name", "preferredFirstName")}

                <div className="onboarding-field">
                  <input
                    value={formData.phone}
                    inputMode="tel"
                    placeholder=" "
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        phone: formatPhone(e.target.value),
                      })
                    }
                    className={errors.phone ? "error" : ""}
                  />
                  <label>Phone</label>

                  <small className="helper">US/Canada 10 digits.</small>
                  {errors.phone && (
                    <span className="error-text">{errors.phone}</span>
                  )}
                </div>

                {renderSelect("Gender", "gender", genderOptions)}

                {/* AddressAutocomplete renders its own input => use static label on mobile */}
                <div className="onboarding-field onboarding-field-static-label">
                  <label className="static-label">Address</label>

                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(val) => {
                      setFormData((prev) => ({ ...prev, address: val }));
                      setStructuredAddr({
                        street: "",
                        city: "",
                        stateProvince: "",
                        postalCode: "",
                        country: "",
                        lat: null,
                        lng: null,
                      });
                    }}
                    onSelect={(place) => {
                      const p = parsePlace(place);

                      setFormData((prev) => ({
                        ...prev,
                        address: p.formattedAddress || p.street || prev.address,
                      }));

                      setStructuredAddr({
                        street: p.street || "",
                        city: p.city || "",
                        stateProvince: p.stateProvince || "",
                        postalCode: p.postalCode || "",
                        country: p.country || "",
                        lat: p.lat ?? null,
                        lng: p.lng ?? null,
                      });
                    }}
                    placeholder="Start typing your address..."
                  />

                  {errors.address && (
                    <span className="error-text">{errors.address}</span>
                  )}
                </div>

                {renderInput("Birth Date", "birthDate", "date")}
              </div>
            )}

            {step === 2 && (
              <div className="onboarding-form-grid">
                {renderInput("Job Title", "jobTitle", "text", {
                  readOnly: locked.jobTitle,
                  helper: locked.jobTitle
                    ? "Managed by your employer"
                    : undefined,
                })}

                {renderSelect(
                  "Group",
                  "group",
                  ensureOption(groupOptions, formData.group),
                  {
                    disabled: locked.group,
                    helper: locked.group
                      ? "Managed by your employer"
                      : undefined,
                  },
                )}
              </div>
            )}

            {step === 3 && (
              <div style={{ padding: "1rem 0" }}>
                <p>
                  <strong>Review your information</strong> and click below to
                  finish onboarding.
                </p>
                {errors.submit && (
                  <span className="error-text">{errors.submit}</span>
                )}
              </div>
            )}

            <div className="onboarding-step-buttons">
              {step > 1 && (
                <button
                  className="onboarding-back-buttons"
                  type="button"
                  onClick={handlePrev}
                >
                  Back
                </button>
              )}

              {step < 3 && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="onboarding-button"
                >
                  Next
                </button>
              )}

              {step === 3 && (
                <button
                  type="submit"
                  disabled={saving}
                  className="onboarding-button"
                >
                  {saving ? "Saving..." : "Complete Onboarding"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
